// Field definitions (DESIGN §2, §9.2, §12). Static; FieldState in the save references these by id.
//
// Grid plots are indexed idx = y * grid.w + x. Prospects are ellipses (cx, cy, rx, ry) in plot units;
// inside the ellipse the reservoir top deepens by dipFtPerPlot per plot of distance from (cx, cy).
// Strata are contiguous from 0 ft; reservoirs sit inside a sand/limestone stratum whose own pore is
// ~normal (8.6) so a dry flank does not behave like a water sand. Water sands carry `water: true`.
//
// Balance notes (5-minute target, DESIGN §0): Oil Creek plot 7 (startPlot) is the crest of prospect A:
// first sand at ~70 ft, qi 15-35 (median ~25). Path: 25 ft gravel (h1, drive pipe), soft blue shale (h2),
// a thin water sand at ~55-62 ft (drive pipe to ~62 ft, max 100 ft), first sand. Sum(h*ft) to 70 ft ~= 137,
// so a spring pole (baseRop 12) needs ~11.5 drilling days at crew/tour factor 1, ~23 at 0.5.
// Bradford pay is never shallower than ~1,290 ft even with -5% jitter, so it needs the iron-braced rig.
import { dateToDay } from '../core/time.js';
import { REGIONS, enableM4Regions } from './regions.js';
import { M2_FIELD_DEFS } from './m2/fields.js';
import { M3_FIELD_DEFS, M3_FIELD_PATCHES } from './m3/fields.js';
import { M4_FIELD_DEFS, M4_FIELD_PATCHES } from './m4/fields.js';
import { m4List, m4Obj } from './m4/gate.js';

// Strata builder: each layer's top is the previous layer's bottom.
// Fracture gradient default grows 1.6 ppg/1,000 ft; real rock tops out around 14-18 ppg, so cap it (M2_SPEC §18.5).
export const FRAC_CAP_PPG = 19;
function stack(layers) {
  let top = 0;
  return layers.map(([bottom, rock, name, hard, pore, stableDays, extra = {}]) => {
    const mid = (top + bottom) / 2;
    const s = { top, bottom, rock, name, hard, pore, frac: +Math.min(FRAC_CAP_PPG, 12 + 1.6 * mid / 1000 + (hard >= 5 ? 0.6 : 0)).toFixed(2), stableDays, h2s: 0, ...extra };
    top = bottom;
    return s;
  });
}

const PA_RES = { di: 0.6, b: 0.5, api: 43, sour: false, rf: 0.15, h2s: 0 }; // h2s in ppm

const defs = [
  // ======================================================================== Oil Creek, PA
  {
    id: 'oil_creek', name: 'Oil Creek, PA', body: 'earth', region: 'pennsylvania', regionId: 'pennsylvania',
    era: 'pioneer', opensDay: dateToDay(1859, 1, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 41.61, lon: -79.66,
    leaseBase: 300, leaseYear: 1859, royalty: 0.125, boomCurve: null, startPlot: 7,
    transport: ['teamster', 'rail@1865', 'gathering_pipe@1865', 'trunk_pipeline@1879'],
    blurb: 'Seneca Oil seeps along a narrow valley above Titusville. Shallow sands, cheap to reach.',
    strata: stack([
      [25,   'gravel',    'Creek gravel & drift',      1, 8.4, 3],
      [55,   'shale',     'Soft blue shale',           2, 8.6, 40],
      [62,   'sand',      'Upper water sand',          4, 9.0, 20, { water: true }],
      [66,   'shale',     'Shale parting',             2, 8.6, 40],
      [100,  'sand',      'Venango First Sand',        4, 8.6, 30],
      [240,  'shale',     'Venango slate & shale',     3, 8.6, 60],
      [256,  'sand',      'Lower water sand',          4, 9.0, 20, { water: true }],
      [325,  'shale',     'Grey shale',                3, 8.6, 60],
      [370,  'sand',      'Venango Second Sand',       5, 8.6, 45],
      [440,  'shale',     'Red & grey shale',          3, 8.7, 50],
      [490,  'sand',      'Venango Third Sand (pebble rock)', 5, 8.7, 45],
      [1400, 'shale',     'Chemung shales & siltstone', 3, 8.8, 70],
      [2600, 'shale',     'Devonian black shale',      4, 9.0, 60],
      [3200, 'limestone', 'Onondaga limestone',        6, 9.0, 120],
      [6000, 'sandstone', 'Oriskany & deeper',         7, 9.2, 150],
    ]),
    prospects: [
      { cx: 1, cy: 1, rx: 1.7, ry: 1.4, reservoirs: [
        { id: 'oc_a_first', name: 'First Sand', top: 70, dipFtPerPlot: 5, thick: 12, ooip: 600000, qiRange: [15, 35], pore: 9.2, gusherChance: 0.03, ...PA_RES },
        { id: 'oc_a_second', name: 'Second Sand', top: 335, dipFtPerPlot: 6, thick: 18, ooip: 1200000, qiRange: [15, 60], pore: 9.3, gusherChance: 0.08, ...PA_RES },
      ] },
      { cx: 4, cy: 2, rx: 1.6, ry: 1.3, reservoirs: [
        { id: 'oc_b_third', name: 'Third Sand', top: 450, dipFtPerPlot: 10, thick: 22, ooip: 3000000, qiRange: [30, 160], pore: 9.6, gusherChance: 0.2, ...PA_RES },
      ] },
      { cx: 3, cy: 0, rx: 1.0, ry: 0.8, reservoirs: [
        { id: 'oc_c_first', name: 'First Sand (east lobe)', top: 78, dipFtPerPlot: 6, thick: 8, ooip: 150000, qiRange: [5, 20], pore: 9.1, gusherChance: 0.01, ...PA_RES },
      ] },
    ],
    seeps: [1, 3, 6, 7, 8, 16, 23],
    hazards: { lightning: 0.0002, fire: 0.0001 },
  },

  // ======================================================================== Pithole, PA
  {
    id: 'pithole', name: 'Pithole Creek, PA', body: 'earth', region: 'pennsylvania', regionId: 'pennsylvania',
    era: 'pioneer', opensDay: dateToDay(1865, 1, 7), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 41.52, lon: -79.58,
    leaseBase: 400, leaseYear: 1865, royalty: 0.125,
    // Lease price multiplier by fractional year: x10 by mid-1865, collapse 1866-67.
    boomCurve: [[1865.0, 1], [1865.25, 4], [1865.5, 10], [1865.8, 9], [1866.0, 6], [1866.4, 3], [1866.8, 1.2], [1867.5, 0.3], [1870, 0.15], [1900, 0.1]],
    transport: ['teamster', 'gathering_pipe@1865', 'rail@1866'],
    blurb: 'A witch-hazel twig found the Frazier well. Six months later 15,000 people live here. Fast in, fast out.',
    strata: stack([
      [40,   'gravel',    'Hillside drift',            1, 8.4, 3],
      [190,  'shale',     'Grey shale',                3, 8.6, 50],
      [205,  'sand',      'Upper water sand',          4, 9.0, 20, { water: true }],
      [395,  'shale',     'Red & grey shale',          3, 8.6, 50],
      [410,  'sand',      'Lower water sand',          4, 9.0, 20, { water: true }],
      [545,  'shale',     'Slate',                     3, 8.7, 50],
      [660,  'sand',      'Pithole grit (Third Sand)', 5, 8.7, 40],
      [1600, 'shale',     'Chemung shales',            3, 8.8, 70],
      [6000, 'shale',     'Devonian shale & deeper',   4, 9.0, 60],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.2, ry: 1.6, reservoirs: [
        { id: 'ph_grit', name: 'Pithole Grit', top: 565, dipFtPerPlot: 14, thick: 20, ooip: 1500000, qiRange: [40, 400], pore: 9.8, gusherChance: 0.3, ...PA_RES, di: 1.2 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0004 },
  },

  // ======================================================================== Bradford, PA
  {
    id: 'bradford', name: 'Bradford, PA', body: 'earth', region: 'pennsylvania', regionId: 'pennsylvania',
    era: 'pioneer', opensDay: dateToDay(1871, 9, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 41.96, lon: -78.64,
    leaseBase: 250, leaseYear: 1871, royalty: 0.125,
    boomCurve: [[1871, 1], [1876, 1.3], [1878, 2.5], [1880, 2], [1883, 1.2], [1890, 1]],
    transport: ['teamster', 'rail@1871', 'gathering_pipe@1871', 'trunk_pipeline@1879'],
    blurb: 'A giant sheet of tight sand under the Tuna Valley. Every plot pays a little, for decades, if you can get down there.',
    strata: stack([
      [60,   'gravel',    'Valley drift',              1, 8.4, 3],
      [420,  'shale',     'Pocono & Catskill red beds', 3, 8.6, 60],
      [440,  'sand',      'Shallow water sand',        4, 9.0, 20, { water: true }],
      [900,  'shale',     'Chemung shale',             3, 8.7, 70],
      [925,  'sand',      'Deep water sand',           4, 9.1, 25, { water: true }],
      [1300, 'shale',     'Grey shale & siltstone',    4, 8.8, 70],
      [1650, 'sand',      'Bradford Third Sand',       5, 8.8, 60],
      [3000, 'shale',     'Devonian shale',            4, 9.0, 60],
      [6000, 'limestone', 'Onondaga & deeper',         6, 9.2, 120],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 3.6, ry: 2.6, reservoirs: [
        { id: 'br_third', name: 'Bradford Third Sand', top: 1360, dipFtPerPlot: 35, thick: 50, ooip: 25000000, qiRange: [10, 30], pore: 9.1, gusherChance: 0.02, ...PA_RES, di: 0.25 },
      ] },
    ],
    seeps: [8, 15],
    hazards: { lightning: 0.0002, fire: 0.0001 },
  },

  // ======================================================================== Lima, OH (sour)
  {
    id: 'lima', name: 'Lima, OH', body: 'earth', region: 'ohio', regionId: 'ohio',
    era: 'pioneer', opensDay: dateToDay(1885, 5, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 40.74, lon: -84.1,
    leaseBase: 200, leaseYear: 1885, royalty: 0.125, boomCurve: null,
    // Sour "skunk oil": quality multiplier 0.5 until the Frasch process (flag 'frasch'), then 0.9.
    sour: { mult: 0.5, flag: 'frasch', multAfter: 0.9 },
    transport: ['teamster', 'rail@1885', 'gathering_pipe@1886'],
    blurb: 'Gas wells in the Trenton limestone turned up oil that stinks of rotten eggs. Plenty of it.',
    strata: stack([
      [50,   'gravel',    'Glacial till',              1, 8.4, 3],
      [520,  'shale',     'Ohio shale',                3, 8.6, 60],
      [540,  'limestone', 'Water-bearing lime',        5, 9.0, 60, { water: true }],
      [1090, 'limestone', 'Niagara & Clinton limestone', 6, 8.7, 150, { h2s: 50 }],
      [1450, 'limestone', 'Trenton limestone',         6, 8.7, 150, { h2s: 120 }],
      [6000, 'dolomite',  'Knox dolomite & deeper',    7, 9.0, 200],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 2.5, ry: 1.8, reservoirs: [
        { id: 'li_trenton', name: 'Trenton Limestone', top: 1120, dipFtPerPlot: 30, thick: 40, ooip: 12000000, qiRange: [20, 150], pore: 9.3, gusherChance: 0.1, ...PA_RES, api: 35, sour: true, h2s: 220, h2sRange: [50, 300], di: 0.5 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0002 },
  },

  // ======================================================================== Oil Springs, Ontario
  {
    id: 'oil_springs', name: 'Oil Springs, Canada West', body: 'earth', region: 'ontario', regionId: 'ontario',
    era: 'pioneer', opensDay: dateToDay(1859, 1, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 42.78, lon: -82.12,
    leaseBase: 300, leaseYear: 1859, royalty: 0.10, boomCurve: [[1859, 1], [1862, 2], [1863, 1.5], [1867, 0.6], [1880, 0.5]],
    transport: ['teamster', 'rail@1866', 'gathering_pipe@1870'],
    blurb: 'Tar-soaked gum beds in the Enniskillen swamp. Safe and shallow; the 1862 Shaw well gushed from the rock at 158 ft.',
    strata: stack([
      [42,   'clay',      'Swamp clay',                2, 8.5, 15],
      [62,   'gravel',    'Gum beds & basal gravel',   1, 8.6, 3],
      [150,  'shale',     'Hamilton shale',            3, 8.6, 50],
      [260,  'limestone', 'Dundee (corniferous) limestone', 6, 8.7, 150],
      [290,  'sand',      'Sulphur water zone',        5, 9.0, 40, { water: true }],
      [1200, 'limestone', 'Detroit River dolomite',    6, 8.8, 150],
      [6000, 'dolomite',  'Salina salt & dolomite',    7, 9.2, 200],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 2.4, ry: 1.7, reservoirs: [
        { id: 'os_gum', name: 'Gum beds', top: 44, dipFtPerPlot: 2, thick: 8, ooip: 150000, qiRange: [2, 10], pore: 8.9, gusherChance: 0, ...PA_RES, api: 30, di: 0.8 },
        { id: 'os_rock', name: 'Rock oil (Dundee)', top: 160, dipFtPerPlot: 8, thick: 20, ooip: 900000, qiRange: [8, 60], pore: 9.4, gusherChance: 0.08, ...PA_RES, api: 33 },
      ] },
    ],
    seeps: [7, 8, 13, 2],
    hazards: { lightning: 0.0002, fire: 0.0001 },
  },

  // ======================================================================== Ploiesti, Wallachia
  {
    id: 'ploiesti', name: 'Ploiești, Wallachia', body: 'earth', region: 'romania', regionId: 'romania',
    era: 'pioneer', opensDay: dateToDay(1859, 1, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 44.94, lon: 26.02,
    leaseBase: 300, leaseYear: 1859, royalty: 0.10, boomCurve: null,
    transport: ['teamster', 'rail@1872', 'gathering_pipe@1895'],
    blurb: 'Peasants have bailed oil from hand-dug pits here for generations. Bring a drill and a rifle.',
    strata: stack([
      [30,   'gravel',    'Prahova gravel',            1, 8.4, 3],
      [140,  'clay',      'Levantine clays',           2, 8.6, 25],
      [160,  'sand',      'Shallow water sand',        4, 9.0, 15, { water: true }],
      [300,  'clay',      'Dacian clay & marl',        3, 8.7, 30],
      [420,  'sand',      'Dacian sands',              4, 8.7, 20],
      [800,  'marl',      'Pontian marl',              3, 8.9, 40],
      [1000, 'sand',      'Meotian sands',             4, 9.0, 25],
      [6000, 'shale',     'Miocene salt & shale',      5, 9.6, 50],
    ]),
    prospects: [
      { cx: 2, cy: 1, rx: 2.2, ry: 1.5, reservoirs: [
        { id: 'pl_dacian', name: 'Dacian sands', top: 310, dipFtPerPlot: 12, thick: 30, ooip: 4000000, qiRange: [15, 120], pore: 9.5, gusherChance: 0.12, ...PA_RES, api: 38 },
        { id: 'pl_meotian', name: 'Meotian sands', top: 810, dipFtPerPlot: 18, thick: 40, ooip: 10000000, qiRange: [60, 500], pore: 10.2, gusherChance: 0.3, ...PA_RES, api: 36, di: 0.4 },
      ] },
    ],
    seeps: [6, 7, 1, 14],
    hazards: { lightning: 0.0002, fire: 0.0003 },
  },

  // ======================================================================== Boryslav, Galicia
  {
    id: 'boryslav', name: 'Boryslav, Galicia', body: 'earth', region: 'galicia', regionId: 'galicia',
    era: 'pioneer', opensDay: dateToDay(1872, 1, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 49.29, lon: 23.42,
    leaseBase: 280, leaseYear: 1872, royalty: 0.10, boomCurve: [[1872, 1], [1895, 1.5], [1908, 3], [1912, 1.5]],
    transport: ['teamster', 'rail@1872', 'gathering_pipe@1890'],
    blurb: 'Ozokerite miners and oil diggers crowd a Carpathian valley. Wax up top, real oil deep down.',
    strata: stack([
      [35,   'gravel',    'Tysmenytsia gravel',        1, 8.4, 3],
      [380,  'clay',      'Ozokerite-bearing clay',    3, 8.7, 20],
      [400,  'sand',      'Water sand',                4, 9.0, 15, { water: true }],
      [1050, 'shale',     'Menilite shale',            4, 8.9, 50],
      [1300, 'sandstone', 'Eocene sandstone',          6, 9.0, 80],
      [6000, 'shale',     'Flysch & deeper',           6, 9.8, 80],
    ]),
    prospects: [
      { cx: 3, cy: 1.5, rx: 2.4, ry: 1.6, reservoirs: [
        { id: 'bo_wax', name: 'Ozokerite seams', top: 200, dipFtPerPlot: 10, thick: 30, ooip: 800000, qiRange: [3, 20], pore: 8.9, gusherChance: 0, ...PA_RES, api: 22 },
        { id: 'bo_eocene', name: 'Eocene sandstone', top: 1100, dipFtPerPlot: 25, thick: 40, ooip: 15000000, qiRange: [60, 600], pore: 10.5, gusherChance: 0.35, ...PA_RES, api: 38, di: 0.5 },
      ] },
    ],
    seeps: [8, 9, 15],
    hazards: { lightning: 0.0002, fire: 0.0004 },
  },

  // ======================================================================== Baku (Balakhany), Russian Empire
  {
    id: 'baku', name: 'Balakhany, Baku', body: 'earth', region: 'baku', regionId: 'baku',
    era: 'pioneer', opensDay: dateToDay(1872, 12, 1), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 40.45, lon: 49.95,
    leaseBase: 400, leaseYear: 1872, royalty: 0.10,
    boomCurve: [[1872.9, 1], [1873.5, 2.5], [1878, 2], [1883.5, 4], [1886, 2.5], [1900, 2]],
    transport: ['teamster', 'gathering_pipe@1878', 'rail@1883'],
    blurb: 'Fire temples, black lakes and loose sands under pressure. Wells here gush for months. So do bandits.',
    strata: stack([
      [20,   'sand',      'Wind-blown sand',           1, 8.4, 2],
      [180,  'clay',      'Absheron clay',             2, 8.7, 20],
      [200,  'sand',      'Brackish water sand',       3, 9.0, 10, { water: true }],
      [330,  'clay',      'Akchagyl clay',             2, 8.8, 20],
      [420,  'sand',      'Upper Productive Series sand', 3, 8.8, 12],
      [650,  'clay',      'Productive Series clay',    3, 9.0, 20],
      [800,  'sand',      'Balakhany suite sands',     4, 9.0, 12],
      [1500, 'clay',      'Pereriva clay & sand',      3, 9.5, 20],
      [6000, 'shale',     'Maikop shale',              4, 11.0, 40],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 3.0, ry: 2.2, reservoirs: [
        { id: 'ba_upper', name: 'Upper Productive sand', top: 340, dipFtPerPlot: 10, thick: 40, ooip: 20000000, qiRange: [100, 600], pore: 10.2, gusherChance: 0.35, ...PA_RES, api: 30, di: 0.5 },
        { id: 'ba_balakhany', name: 'Balakhany suite', top: 665, dipFtPerPlot: 18, thick: 80, ooip: 80000000, qiRange: [300, 3000], pore: 11.0, gusherChance: 0.55, ...PA_RES, api: 32, di: 0.45 },
      ] },
    ],
    seeps: [7, 8, 9, 14, 15, 3],
    hazards: { lightning: 0.0001, fire: 0.0008 },
  },

  // ======================================================================== Telaga Said, Sumatra
  {
    id: 'telaga_said', name: 'Telaga Said, Sumatra', body: 'earth', region: 'sumatra', regionId: 'sumatra',
    era: 'pioneer', opensDay: dateToDay(1885, 6, 15), grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 3.95, lon: 98.2,
    leaseBase: 300, leaseYear: 1885, royalty: 0.08, boomCurve: [[1885.4, 1], [1890, 2], [1895, 1.5]],
    transport: ['teamster', 'gathering_pipe@1892'],
    blurb: 'Zijlker’s concession in the Langkat jungle. Fever, tigers and Acehnese raiders guard a very rich sand.',
    strata: stack([
      [30,   'clay',      'Jungle laterite',           2, 8.4, 5],
      [200,  'clay',      'Volcanic tuff & clay',      3, 8.6, 20],
      [215,  'sand',      'Water sand',                4, 9.0, 15, { water: true }],
      [380,  'shale',     'Keutapang shale',           3, 8.8, 30],
      [520,  'sand',      'Keutapang sand',            4, 8.8, 20],
      [1500, 'shale',     'Baong shale',               4, 9.2, 40],
      [6000, 'limestone', 'Belumai limestone & deeper', 6, 9.8, 100],
    ]),
    prospects: [
      { cx: 2, cy: 2, rx: 2.4, ry: 1.6, reservoirs: [
        { id: 'ts_keutapang', name: 'Keutapang sand', top: 395, dipFtPerPlot: 14, thick: 50, ooip: 30000000, qiRange: [80, 800], pore: 10.0, gusherChance: 0.3, ...PA_RES, api: 40, di: 0.4 },
      ] },
    ],
    seeps: [13, 14, 19],
    hazards: { lightning: 0.0004, fire: 0.0002, disease: 0.0005 },
  },
];

export const FIELDS = {};
for (const f of defs) {
  f.royalty ??= REGIONS[f.regionId].royalty;
  f.leaseMult = REGIONS[f.regionId].leaseMult;      // applied on top of leaseBase by the lease-price formula
  f.boomCurve ??= null;
  f.sour ??= null;
  FIELDS[f.id] = f;
}

// ------------------------------------------------------------------ M2 fields (Gusher & Rotary, 1901-1945)
// Definitions live in src/data/m2/fields.js. They are attached to FIELDS as NON-ENUMERABLE properties:
// FIELDS.spindletop resolves (news, tests, lookups by id), but Object.values/keys(FIELDS), openFields()
// and newGame() do not see them, so M1 seeded games (which generate every enumerable field with the
// shared RNG) are bit-for-bit unchanged. enableM2Fields() makes them ordinary entries; see docs/M2_SPEC.md §0.
export const M2_FIELDS = {};
for (const f of M2_FIELD_DEFS) {
  f.royalty ??= REGIONS[f.regionId].royalty;
  f.leaseMult = REGIONS[f.regionId].leaseMult;
  f.boomCurve ??= null;
  f.sour ??= null;
  M2_FIELDS[f.id] = f;
  Object.defineProperty(FIELDS, f.id, { value: f, enumerable: false, writable: true, configurable: true });
}
let m2Enabled = false;
export const m2FieldsEnabled = () => m2Enabled;
// Idempotent. Call before newGame() (or migrate saves with per-field generation, M2_SPEC §0).
export function enableM2Fields() {
  if (m2Enabled) return false;
  m2Enabled = true;
  for (const f of M2_FIELD_DEFS) {
    Object.defineProperty(FIELDS, f.id, { value: f, enumerable: true, writable: true, configurable: true });
    defs.push(f);
  }
  return true;
}

// ------------------------------------------------------------------ M3 fields (Postwar & Offshore, 1946-1999)
// Definitions live in src/data/m3/fields.js. Same gating as M2: non-enumerable until enableM3Fields() (M3_SPEC §0).
// enableM3Fields() also enables M2 first (so FIELDS order stays M1, M2, M3) and applies M3_FIELD_PATCHES to existing
// fields: transport entries dated >= 1946 are APPENDED, missing keys added (nothing before 1946 changes).
export const M3_FIELDS = {};
for (const f of M3_FIELD_DEFS) {
  f.royalty ??= REGIONS[f.regionId].royalty;
  f.leaseMult = REGIONS[f.regionId].leaseMult;
  f.boomCurve ??= null;
  f.sour ??= null;
  M3_FIELDS[f.id] = f;
  Object.defineProperty(FIELDS, f.id, { value: f, enumerable: false, writable: true, configurable: true });
}
let m3Enabled = false;
export const m3FieldsEnabled = () => m3Enabled;
export function enableM3Fields() {
  if (m3Enabled) return false;
  enableM2Fields();
  m3Enabled = true;
  for (const f of M3_FIELD_DEFS) {
    Object.defineProperty(FIELDS, f.id, { value: f, enumerable: true, writable: true, configurable: true });
    defs.push(f);
  }
  for (const [id, patch] of Object.entries(M3_FIELD_PATCHES)) {
    const f = FIELDS[id];
    if (!f) continue;
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'transport') { for (const t of v) if (!f.transport.includes(t)) f.transport.push(t); }
      else f[k] ??= v;
    }
  }
  return true;
}

// ------------------------------------------------------------------ M4 fields (Shale & Deepwater, 2000-2029)
// Definitions live in src/data/m4/fields.js. Same gating as M2/M3: non-enumerable until enableM4Fields() (M4_SPEC §0).
// enableM4Fields() enables M3 (and so M2) first, makes the six M4 regions enumerable, and applies M4_FIELD_PATCHES to
// existing fields (transport entries dated >= 2000 appended, missing keys added; nothing before 2000 changes).
// Not called anywhere yet: the M4 sim agent calls it in sim/defs.js.
export const M4_FIELDS = {};
for (const f of m4List(M4_FIELD_DEFS)) {
  f.royalty ??= REGIONS[f.regionId].royalty;
  f.leaseMult = REGIONS[f.regionId].leaseMult;
  f.boomCurve ??= null;
  f.sour ??= null;
  M4_FIELDS[f.id] = f;
  Object.defineProperty(FIELDS, f.id, { value: f, enumerable: false, writable: true, configurable: true });
}
let m4Enabled = false;
export const m4FieldsEnabled = () => m4Enabled;
export function enableM4Fields() {
  if (m4Enabled) return false;
  enableM3Fields();
  enableM4Regions();
  m4Enabled = true;
  for (const f of m4List(M4_FIELD_DEFS)) {
    Object.defineProperty(FIELDS, f.id, { value: f, enumerable: true, writable: true, configurable: true });
    defs.push(f);
  }
  for (const [id, patch] of Object.entries(m4Obj(M4_FIELD_PATCHES))) {
    const f = FIELDS[id];
    if (!f) continue;
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'transport') { for (const t of v) if (!f.transport.includes(t)) f.transport.push(t); }
      else f[k] ??= v;
    }
  }
  return true;
}

// Fields open (discovered and leasable) on `day`, in definition order.
export function openFields(day) {
  return defs.filter((f) => f.opensDay <= day);
}

// Lease boom multiplier for a field in fractional `year` (1 when no curve). Linear between points, clamped.
export function boomMult(field, year) {
  const f = typeof field === 'string' ? FIELDS[field] : field;
  const c = f.boomCurve;
  if (!c) return 1;
  if (year <= c[0][0]) return c[0][1];
  if (year >= c[c.length - 1][0]) return c[c.length - 1][1];
  for (let i = 1; i < c.length; i++) {
    if (year <= c[i][0]) {
      const [y0, v0] = c[i - 1], [y1, v1] = c[i];
      return v0 + (v1 - v0) * (year - y0) / (y1 - y0);
    }
  }
  return 1;
}

// Transport def ids available at a field in `year`, parsed from "id@year" entries.
export function fieldTransports(field, year) {
  const f = typeof field === 'string' ? FIELDS[field] : field;
  return f.transport
    .map((t) => { const [id, y] = t.split('@'); return { id, year: y ? +y : 0 }; })
    .filter((t) => year >= t.year)
    .map((t) => t.id);
}

// Stratum at a given (un-jittered) depth.
export function stratumAt(field, depth) {
  const f = typeof field === 'string' ? FIELDS[field] : field;
  return f.strata.find((s) => depth >= s.top && depth < s.bottom) ?? f.strata[f.strata.length - 1];
}

// Plot helpers
export const plotXY = (field, idx) => ({ x: idx % field.grid.w, y: Math.floor(idx / field.grid.w) });

// Index of the prospect containing plot idx (first match), or -1. Distance in plot units from its center.
export function prospectAt(field, idx) {
  const { x, y } = plotXY(field, idx);
  for (let i = 0; i < field.prospects.length; i++) {
    const p = field.prospects[i];
    const dx = (x - p.cx) / p.rx, dy = (y - p.cy) / p.ry;
    if (dx * dx + dy * dy <= 1) return { prospectIdx: i, dist: Math.hypot(x - p.cx, y - p.cy) };
  }
  return { prospectIdx: -1, dist: 0 };
}
